// src/lib/supabase.js - Updated to work with your existing email_signups table
import { createClient } from '@supabase/supabase-js'
import { emailService as welcomeEmailService } from './email-service.js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseKey)


// 🆕 CRITICAL FIX: Expose Supabase globally for real-time subscriptions
if (typeof window !== 'undefined') {
  window.supabase = supabase;
  window.supabaseClient = supabase; // Backup reference
  console.log('✅ Supabase client exposed globally for real-time subscriptions');
}


// Email service for collecting signups - matches your existing table structure
export const emailService = {
  


async addEmail(email, source = 'website', metadata = {}) {
  try {
    // Validate email format (client-side validation)
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email)) {
      return {
        success: false,
        error: 'Please enter a valid email address',
        isDuplicate: false
      };
    }

    console.log('📧 Calling Edge Function for email signup:', { 
      email, 
      source, 
      metadata_event_title: metadata.event_title,
      full_metadata: metadata 
    });

    // 🚀 CALL EDGE FUNCTION (not Brevo directly)
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-signup`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          source,
          metadata: {
            ...metadata, // ← Move this FIRST so it doesn't get overridden
            signup_page: window.location.pathname,
            campaign_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
            campaign_medium: new URLSearchParams(window.location.search).get('utm_medium') || 'organic',
            campaign_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || 'none',
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            user_agent: navigator.userAgent,
            referrer: document.referrer || 'direct'
          }
        })
      }
    );

    const result = await response.json();

    // 🔥 CRITICAL FIX: Handle subscription requirements FIRST, before success/error logic
    if (result.requiresSubscription) {
      console.log('🚫 Subscription required - returning subscription redirect response');
      return {
        success: false,
        requiresSubscription: true,
        hasSubscription: result.hasSubscription || false,
        message: result.message || 'Active subscription required for this event',
        subscriptionUrl: result.subscriptionUrl || '/subscription',
        eventInfo: result.eventInfo || null,
        // Preserve any other fields from the response
        currentCount: result.currentCount,
        reactivated: result.reactivated,
        brevo_result: result.brevo_result,
        email_sent: result.email_sent,
        eventContext: result.eventContext
      };
    }

    if (result.success) {
      console.log('✅ Edge Function success:', result);
      return {
        success: true,
        data: result.data,
        reactivated: result.reactivated || false,
        message: result.message,
        reservation: result.reservation // 🔥 IMPORTANT: Include reservation data
      };
    } else {
      console.error('❌ Edge Function error:', result);
      return {
        success: false,
        error: result.error,
        isDuplicate: result.isDuplicate || false,
        reservation: result.reservation // 🔥 IMPORTANT: Include reservation data for duplicates
      };
    }

  } catch (error) {
    console.error('❌ Network error calling Edge Function:', error);
    return {
      success: false,
      error: 'Network error occurred. Please check your connection and try again.',
      isDuplicate: false
    };
  }
},

 

 
  async getEmailCount(status = 'active') {
    try {
      const { count, error } = await supabase
        .from('email_signups')
        .select('id', { count: 'exact', head: true }) // ← Changed from '*' to 'id'
        .eq('status', status);
  
      if (error) {
        console.error('Error getting email count:', error);
        return 0;
      }
  
      return count || 0;
    } catch (error) {
      console.error('Error getting email count:', error);
      return 0;
    }
  },

  async getRecentSignups(limit = 10, status = 'active') {
    try {
      const { data, error } = await supabase
        .from('email_signups')
        .select('email, source, subscribed_at, metadata')
        .eq('status', status)
        .order('subscribed_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting recent signups:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error getting recent signups:', error);
      return [];
    }
  },

  async getSignupStats(days = 30) {
    try {
      const { data, error } = await supabase
        .from('email_signup_stats')
        .select('*')
        .gte('signup_date', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
        .order('signup_date', { ascending: false });

      if (error) {
        console.error('Error getting signup stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error getting signup stats:', error);
      return [];
    }
  },

  // Add this to your existing emailService object in src/lib/supabase.js

// 🆕 NEW: Enhanced unsubscribe function with admin notifications
async unsubscribeEmail(email, options = {}) {
  try {
    console.log('🚫 Processing unsubscribe for:', email);
    
    // Validate email format
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email)) {
      return { 
        success: false, 
        error: 'Please enter a valid email address',
        code: 'INVALID_EMAIL'
      };
    }

    // Extract options with defaults
    const {
      reason = 'not_specified',
      method = 'email_link',
      feedback = null,
      sourceType = 'email_unsubscribe_link',
      userAgent = navigator?.userAgent || null
    } = options;

    // Call the comprehensive database function
    const { data, error } = await supabase.rpc('process_unsubscribe', {
      p_email: email.toLowerCase().trim(),
      p_reason: reason,
      p_method: method,
      p_feedback: feedback,
      p_source_type: sourceType,
      p_user_agent: userAgent,
      p_ip_address: null // Client-side can't get real IP
    });

    if (error) {
      console.error('❌ Database unsubscribe error:', error);
      return { 
        success: false, 
        error: `Failed to unsubscribe: ${error.message}`,
        code: 'DATABASE_ERROR'
      };
    }

    console.log('✅ Database unsubscribe result:', data);

    if (!data.success) {
      return {
        success: false,
        error: data.error || 'Failed to process unsubscribe',
        code: data.code || 'UNSUBSCRIBE_FAILED'
      };
    }

    // 🚀 Send admin notification email via Edge Function
    try {
      console.log('📧 Sending admin notification for unsubscribe...');
      
      const adminNotificationResult = await this.sendUnsubscribeNotification({
        email: email.toLowerCase().trim(),
        reason,
        feedback,
        unsubscribedAt: data.unsubscribed_at,
        trackingId: data.tracking_id,
        method
      });

      if (adminNotificationResult.success) {
        console.log('✅ Admin notification sent successfully');
      } else {
        console.warn('⚠️ Admin notification failed:', adminNotificationResult.error);
        // Don't fail the unsubscribe if notification fails
      }
    } catch (notificationError) {
      console.warn('⚠️ Admin notification exception:', notificationError);
      // Don't fail the unsubscribe if notification fails
    }

    return {
      success: true,
      message: data.message || 'Successfully unsubscribed',
      email: email.toLowerCase().trim(),
      unsubscribedAt: data.unsubscribed_at,
      trackingId: data.tracking_id,
      alreadyUnsubscribed: data.already_unsubscribed || false
    };

  } catch (error) {
    console.error('❌ Unsubscribe exception:', error);
    return {
      success: false,
      error: 'Network error occurred. Please try again.',
      code: 'NETWORK_ERROR'
    };
  }
},

// 🆕 NEW: Send unsubscribe notification to admin
async sendUnsubscribeNotification(unsubscribeData) {
  try {
    console.log('📧 Calling Edge Function for unsubscribe notification');
    
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unsubscribe-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          ...unsubscribeData,
          adminEmail: 'hello@connectco.app',
          timestamp: new Date().toISOString(),
          userAgent: navigator?.userAgent,
          referrer: document.referrer || 'direct',
          currentUrl: window.location.href
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Admin notification response error:', response.status, errorText);
      return { 
        success: false, 
        error: `HTTP ${response.status}: ${errorText}` 
      };
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('✅ Admin notification sent:', result);
      return { success: true, data: result };
    } else {
      console.error('❌ Admin notification failed:', result);
      return { 
        success: false, 
        error: result.error || 'Failed to send admin notification' 
      };
    }

  } catch (error) {
    console.error('❌ Admin notification exception:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to send admin notification' 
    };
  }
},

// 🆕 NEW: Get unsubscribe analytics (for admin dashboard)
async getUnsubscribeStats(daysBack = 30) {
  try {
    const { data, error } = await supabase.rpc('get_unsubscribe_stats', {
      p_days_back: daysBack
    });

    if (error) {
      console.error('❌ Error getting unsubscribe stats:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data[0] || {} };
  } catch (error) {
    console.error('❌ Exception getting unsubscribe stats:', error);
    return { success: false, error: error.message };
  }
},

// 🆕 NEW: Get recent unsubscribes (for monitoring)
async getRecentUnsubscribes(limit = 10) {
  try {
    const { data, error } = await supabase
      .from('unsubscribe_analytics')
      .select('*')
      .order('unsubscribed_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('❌ Error getting recent unsubscribes:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Exception getting recent unsubscribes:', error);
    return { success: false, error: error.message };
  }
},

// UPDATED: Replace the existing simple unsubscribeEmail function with the enhanced version above


 

};

// Helper function to get client IP (for metadata)
async function getClientIP() {
  try {
    const response = await fetch('https://ipapi.co/ip/');
    return await response.text();
  } catch (error) {
    return 'unknown';
  }
}

// Real-time subscription for email signups (optional)
export const subscribeToEmailSignups = (callback) => {
  return supabase
    .channel('email_signups')
    .on('postgres_changes', 
      { event: 'INSERT', schema: 'public', table: 'email_signups' }, 
      callback
    )
    .subscribe();
};

// Enhanced analytics functions
export const emailAnalytics = {
  async getSourceBreakdown() {
    try {
      const { data, error } = await supabase
        .from('email_signups')
        .select('source, status')
        .eq('status', 'active');

      if (error) throw error;

      const breakdown = data.reduce((acc, item) => {
        acc[item.source] = (acc[item.source] || 0) + 1;
        return acc;
      }, {});

      return breakdown;
    } catch (error) {
      console.error('Error getting source breakdown:', error);
      return {};
    }
  },

  async getGrowthData(days = 30) {
    try {
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      
      const { data, error } = await supabase
        .from('email_signups')
        .select('subscribed_at')
        .gte('subscribed_at', startDate.toISOString())
        .eq('status', 'active')
        .order('subscribed_at', { ascending: true });

      if (error) throw error;

      // Group by day
      const dailySignups = data.reduce((acc, item) => {
        const date = new Date(item.subscribed_at).toDateString();
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      return dailySignups;
    } catch (error) {
      console.error('Error getting growth data:', error);
      return {};
    }
  }
};


// 🆕 NEW: Event cancellation service 
const cancellationService = {
  
  // Request cancellation (sends confirmation email)
  async requestCancellation(email, eventSlug) {
    try {
      console.log('🚫 Requesting cancellation for:', { email, eventSlug });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-cancellation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            email: email.toLowerCase().trim(),
            eventSlug: eventSlug
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        return {
          success: true,
          message: result.message
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to process cancellation request'
        };
      }

    } catch (error) {
      console.error('❌ Cancellation request error:', error);
      return {
        success: false,
        error: 'Network error occurred. Please try again.'
      };
    }
  },

  // Confirm cancellation (from email link)
  async confirmCancellation(token) {
    try {
      console.log('✅ Confirming cancellation with token');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-cancellation?action=confirm&token=${token}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        return {
          success: true,
          message: result.message,
          eventSlug: result.eventSlug,
          newCount: result.newCount
        };
      } else {
        return {
          success: false,
          error: result.error || 'Failed to confirm cancellation'
        };
      }

    } catch (error) {
      console.error('❌ Cancellation confirmation error:', error);
      return {
        success: false,
        error: 'Network error occurred. Please try again.'
      };
    }
  },

  // Get user's active registrations
  async getUserRegistrations(email) {
    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('id, event_slug, event_title, event_date, rsvp_status, created_at')
        .eq('email', email.toLowerCase().trim())
        .eq('rsvp_status', 'interested')
        .order('event_date', { ascending: true });

      if (error) {
        console.error('Error fetching user registrations:', error);
        return { success: false, error: 'Failed to fetch registrations' };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching user registrations:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }
};

const enhancedEmailAnalytics = {
  // Add to existing emailAnalytics object
  
  async getEventRegistrationStats() {
    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('event_slug, rsvp_status, created_at')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      if (error) throw error;

      const stats = data.reduce((acc, rsvp) => {
        const eventSlug = rsvp.event_slug;
        if (!acc[eventSlug]) {
          acc[eventSlug] = { 
            total: 0, 
            active: 0, 
            cancelled: 0,
            cancellationRate: 0 
          };
        }
        acc[eventSlug].total++;
        if (rsvp.rsvp_status === 'interested') {
          acc[eventSlug].active++;
        } else if (rsvp.rsvp_status === 'cancelled') {
          acc[eventSlug].cancelled++;
        }
        return acc;
      }, {});

      // Calculate cancellation rates
      Object.keys(stats).forEach(eventSlug => {
        const stat = stats[eventSlug];
        stat.cancellationRate = stat.total > 0 ? (stat.cancelled / stat.total * 100).toFixed(1) : 0;
      });

      return stats;
    } catch (error) {
      console.error('Error getting event registration stats:', error);
      return {};
    }
  },

  async getCancellationTrends(days = 30) {
    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('cancelled_at, event_slug')
        .not('cancelled_at', 'is', null)
        .gte('cancelled_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
        .order('cancelled_at', { ascending: true });

      if (error) throw error;

      const dailyCancellations = data.reduce((acc, cancellation) => {
        const date = new Date(cancellation.cancelled_at).toDateString();
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      return dailyCancellations;
    } catch (error) {
      console.error('Error getting cancellation trends:', error);
      return {};
    }
  }
};

// 11-25-25
// 🆕 PUSH NOTIFICATION SERVICE
export const pushNotificationService = {
  
  // Subscribe to push notifications
  async subscribe(subscriptionData) {
    try {
      const { email, endpoint, keys, browser, timezone, language, userAgent } = subscriptionData;
      
      const { data, error } = await supabase
        .from('push_subscriptions')
        .upsert({
          email,
          endpoint,
          keys,
          browser,
          timezone,
          language,
          user_agent: userAgent,
          platform: 'web',
          device_type: this.getDeviceType(),
          is_active: true,
          subscribed_at: new Date().toISOString()
        }, {
          onConflict: 'endpoint'
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving push subscription:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Push subscription saved:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Exception saving push subscription:', error);
      return { success: false, error: error.message };
    }
  },

  // Unsubscribe from push notifications
  async unsubscribe(endpoint) {
    try {
      const { error } = await supabase
        .from('push_subscriptions')
        .update({ 
          is_active: false, 
          unsubscribed_at: new Date().toISOString() 
        })
        .eq('endpoint', endpoint);

      if (error) {
        console.error('Error unsubscribing:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Exception unsubscribing:', error);
      return { success: false, error: error.message };
    }
  },

  // Check if endpoint is subscribed
  async isSubscribed(endpoint) {
    try {
      const { data, error } = await supabase
        .from('push_subscriptions')
        .select('is_active')
        .eq('endpoint', endpoint)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') { // Ignore "not found" error
        console.error('Error checking subscription:', error);
        return false;
      }

      return data?.is_active || false;
    } catch (error) {
      console.error('Exception checking subscription:', error);
      return false;
    }
  },

  // Get today's prompt
  async getTodaysPrompt() {
    try {
      const { data, error } = await supabase
        .from('daily_prompts')
        .select('*')
        .eq('scheduled_for', new Date().toISOString().split('T')[0])
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching prompt:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Exception fetching prompt:', error);
      return null;
    }
  },

  // Submit response to prompt
  async submitResponse(promptId, email, responseText) {
    try {
      const { data, error } = await supabase
        .from('prompt_responses')
        .insert({
          prompt_id: promptId,
          email,
          response_text: responseText,
          responded_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error submitting response:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception submitting response:', error);
      return { success: false, error: error.message };
    }
  },

  // Get recent responses for a prompt
  async getPromptResponses(promptId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('prompt_responses')
        .select('*')
        .eq('prompt_id', promptId)
        .order('responded_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching responses:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Exception fetching responses:', error);
      return [];
    }
  },

  // Helper: Detect device type
  getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
      return 'mobile';
    } else if (/ipad|tablet|playbook|silk/i.test(userAgent)) {
      return 'tablet';
    }
    return 'desktop';
  }
};